import React, { FC } from 'react';
import {
  useTrackElement,
  usePersistedSessionState,
} from '@edreamsodigeo/frontend-core';
import {
  DrawerBottomSheet,
  DrawerBottomSheetContent,
  useDrawer,
  Spinner,
  Flex,
  Glyph,
  DrawerBottomSheetHeader,
  Box,
} from '@edreamsodigeo/prisma-design-system';
import AlternativeItineraryDoubleCardLayerMobileContent, {
  AlternativeItineraryDoubleCardDropdownLayerMobileContent,
} from './AlternativeItineraryDoubleCardLayerMobileContent';
import { useAlternativeItinerary } from 'clientState/providers/AlternativeItineraryProvider/AlternativeItineraryCalculatorProvider';
import {
  AlternativeItineraryDoubleCardLayerMobileProps,
  AlternativeItineraryLoadingProps,
} from './types';
import { useLaunchAlternativeAbandonmentEvents } from 'clientState/hooks/useAlternativeAbandonmentEvents/useAlternativeAbandonmentEvents';
import { AlternativeItineraryDoubleCardShoppingCartLoading as ShoppingCartLoading } from './AlternativeItineraryDoubleCardShoppingCartLoading';
import { PageType } from 'clientState/providers/AlternativeItineraryProvider/hooks';
import { getSearchUrl } from '@components/AlternativeFlight/helpers';

const AlternativeItineraryDoubleCardLayerMobileLoading: FC<
  AlternativeItineraryLoadingProps
> = ({ innerHeightPercentage = 0.75 }) => {
  return (
    <DrawerBottomSheetContent>
      <Flex
        flexDirection="row"
        alignItems="center"
        height={`${window.innerHeight * innerHeightPercentage}px`}
        width="100%"
      >
        <Flex flexDirection="column" alignItems="center" width="100%">
          <Spinner size="xlarge" />
        </Flex>
      </Flex>
    </DrawerBottomSheetContent>
  );
};

const AlternativeItineraryDoubleCardLayerMobile: FC<
  AlternativeItineraryDoubleCardLayerMobileProps
> = ({
  isOpen,
  onClose,
  itinerary,
  valueProposition,
  valuePropositions,
  additionalInformation,
  discountAmount,
  isBrokenFlow,
  nonIssuable = true,
  isUnavailable = false,
  hasCloseIcon = true,
  showContinueButton,
  selectedItinerary,
  loading,
  showLoadingBar,
  isShoppingCartLoading = false,
  onContinue,
  onChange,
  overrideAction,
  simpleUI = false,
  trackingHandler,
  pageName = PageType.FlightsResults,
}) => {
  const ContentComponent = simpleUI
    ? AlternativeItineraryDoubleCardDropdownLayerMobileContent
    : AlternativeItineraryDoubleCardLayerMobileContent;
  const { altSearchId } = useAlternativeItinerary();
  const backgroundColor = 'neutral.20';
  const { state, transitionDuration } = useDrawer({
    isOpen: isOpen,
    transitionDuration: 300,
  });

  const displayTracking = useTrackElement(
    ['display'],
    `alternative-flights-layer-searchId-${altSearchId}-itineraryId-${selectedItinerary?.id}-alternativeItineraryId-${itinerary?.id}-${valueProposition}-discount:${discountAmount?.amount}${discountAmount?.currency}`
  );
  const trackingRef =
    altSearchId && itinerary && isOpen && !loading ? displayTracking : null;

  const alternativeAbandonmentEvents = useLaunchAlternativeAbandonmentEvents();
  const [searchParameters] = usePersistedSessionState('searchParameters');
  const handleCloseBackdrop = () => {
    trackingHandler('discard', 'backdrop', 'OP');
    alternativeAbandonmentEvents();
    onClose();
  };
  const handleCloseIcon = () => {
    trackingHandler('discard', 'cross', 'CC');
    alternativeAbandonmentEvents();
    onClose();
  };

  const handleGoBackIcon = () => {
    const searchUrl = getSearchUrl(searchParameters);
    window.location.assign(searchUrl);
    alternativeAbandonmentEvents();
  };

  const goBack =
    simpleUI && nonIssuable && pageName !== PageType.FlightsResults;

  return (
    <div ref={trackingRef}>
      <DrawerBottomSheet
        state={state}
        transitionDuration={transitionDuration}
        onBackdropClick={handleCloseBackdrop}
        sx={{
          backgroundColor: backgroundColor,
          maxHeight: '95%',
        }}
      >
        <DrawerBottomSheetHeader elevated={false}>
          {(hasCloseIcon || simpleUI) && (
            <Box sx={{ height: '8px' }}>
              <Flex
                className="random"
                data-testid="close-icon"
                top="minus_1x_small"
                position="absolute"
                onClick={() =>
                  goBack ? handleGoBackIcon() : handleCloseIcon()
                }
              >
                <Glyph
                  category={goBack ? 'Arrows' : 'UIActions'}
                  name={goBack ? 'Left' : 'Clear'}
                  size="medium"
                  color="neutral.90"
                  sx={{ zIndex: 1 }}
                />
              </Flex>
            </Box>
          )}
        </DrawerBottomSheetHeader>
        {(loading || !itinerary) && (
          <AlternativeItineraryDoubleCardLayerMobileLoading
            innerHeightPercentage={simpleUI ? 0.65 : 0.75}
          />
        )}
        {!loading && itinerary && !isShoppingCartLoading && (
          <ContentComponent
            itinerary={itinerary}
            valueProposition={valueProposition}
            valuePropositions={valuePropositions}
            additionalInformation={additionalInformation}
            discountAmount={discountAmount}
            isBrokenFlow={isBrokenFlow}
            nonIssuable={nonIssuable}
            isUnavailable={isUnavailable}
            showContinueButton={showContinueButton}
            selectedItinerary={selectedItinerary}
            backgroundColor={backgroundColor}
            onContinue={onContinue}
            onChange={onChange}
            overrideAction={overrideAction}
            trackingHandler={trackingHandler}
            simpleUI={simpleUI}
          />
        )}
        {(showLoadingBar || (isShoppingCartLoading && !loading)) && (
          <Flex
            height={`${window.innerHeight * 0.75}px`}
            alignItems="center"
            justifyContent="center"
          >
            <ShoppingCartLoading />
          </Flex>
        )}
      </DrawerBottomSheet>
    </div>
  );
};

export default AlternativeItineraryDoubleCardLayerMobile;
